import { useState, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { GET_ALL_METRICS, GET_DEFAULT_METRICS } from '@/graphql/audience-targeting-metrics-queries'

// Helper function to get default column preferences (spend and ROAS)
export const getDefaultColumnPreferences = () => {
    return [
        {
            id: '6ba7b81a-9dad-11d1-80b4-00c04fd430c8',
            name: 'Amount Spent',
            apiKey: 'spend',
            dataType: 'currency',
            isDefault: true,
        },
        {
            id: '6ba7b819-9dad-11d1-80b4-00c04fd430c8',
            name: 'ROAS',
            apiKey: 'roas',
            dataType: 'number',
            isDefault: true,
        },
    ]
}

export const useColumnPreferences = (widget, setStepContentData, getStepContentData) => {
    const dataKey = widget ? `columnPreferences-${widget}` : 'columnPreferences'

    // GraphQL queries for metrics
    const { data: allMetricsData, loading: allMetricsLoading } = useQuery(GET_ALL_METRICS)
    const { data: defaultMetricsData, loading: defaultMetricsLoading } = useQuery(GET_DEFAULT_METRICS)

    // State for selected columns
    const [selectedColumns, setSelectedColumns] = useState([])
    const [isLoading, setIsLoading] = useState(true)

    // Initialize from wizard context or defaults
    useEffect(() => {
        try {
            const stepData = getStepContentData()
            const stored = stepData[dataKey]
            if (stored) {
                setSelectedColumns(stored)
            } else {
                // Use default metrics if no stored preferences
                const defaultColumns = defaultMetricsData.getDefaultMetrics.map((metric) => ({
                    id: metric.id,
                    name: metric.name,
                    apiKey: metric.apiKey,
                    dataType: metric.dataType,
                    isDefault: metric.isDefault,
                }))
                setSelectedColumns(defaultColumns)
            }
        } catch (error) {
            console.warn('Failed to load column preferences from wizard context:', error)
            // Fallback to defaults
            if (defaultMetricsData?.getDefaultMetrics) {
                const defaultColumns = defaultMetricsData.getDefaultMetrics.map((metric) => ({
                    id: metric.id,
                    name: metric.name,
                    apiKey: metric.apiKey,
                    dataType: metric.dataType,
                    isDefault: metric.isDefault,
                }))
                setSelectedColumns(defaultColumns)
            }
        } finally {
            setIsLoading(false)
        }
    }, [defaultMetricsData, defaultMetricsLoading, dataKey, widget, getStepContentData])

    // Save to wizard context whenever selectedColumns changes
    useEffect(() => {
        if (!isLoading && selectedColumns.length > 0) {
            setStepContentData({ [dataKey]: selectedColumns })
        }
    }, [selectedColumns, dataKey, isLoading, setStepContentData])

    // Function to update selected columns
    const updateSelectedColumns = (newColumns) => {
        setSelectedColumns(newColumns)
    }

    // Function to reset to defaults - specifically set amount spent and ROAS
    const resetToDefaults = () => {
        if (allMetricsData?.getAllMetrics) {
            // Filter for specific metrics: amount spent (spend) and ROAS (roas)
            const targetApiKeys = ['spend', 'roas']
            const defaultColumns = allMetricsData.getAllMetrics
                .filter((metric) => targetApiKeys.includes(metric.apiKey))
                .map((metric) => ({
                    id: metric.id,
                    name: metric.name,
                    apiKey: metric.apiKey,
                    dataType: metric.dataType,
                    isDefault: metric.isDefault,
                }))
            setSelectedColumns(defaultColumns)
        }
    }

    // Function to check if a metric is selected
    const isColumnSelected = (metricId) => selectedColumns.some((column) => column.id === metricId)

    return {
        // Data
        selectedColumns,
        allMetrics: allMetricsData?.getAllMetrics || [],
        defaultMetrics: defaultMetricsData?.getDefaultMetrics || [],

        // Loading states
        isLoading: isLoading || allMetricsLoading || defaultMetricsLoading,

        // Functions
        updateSelectedColumns,
        resetToDefaults,
        isColumnSelected,
    }
}
